import { connect, FormikValues } from 'formik'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import { Select } from '@/components/Form'
import { FormLabel, Row } from '@/components/StyledComponents'
import {
  StyledDateTimePicker,
  StyledRadio,
  StyledRadioInput
} from '@/pages/SpaceAppBuilder/components/StyledComponents'

import { ScheduledIntervalType } from './interfaces'
import messages from './messages'
import { getDaysOfWeekNumeric, getNumericRangeOptions, getWeekOfMonth } from './utils'

const StyledMonthlyRadio = styled(StyledRadio)`
  flex-direction: column;
  justify-content: center;
`
const StyledRow = styled(Row)`
  justify-content: flex-start;
  margin-bottom: 5px;

  && {
    > * {
      margin-right: 10px;
    }
  }
`
const StyledSelect = styled(Select).withConfig({
  shouldForwardProp: prop => prop !== 'isPlaceholder'
})`
  && {
    flex-basis: 40%;
    margin-left: 10px !important;
    .field__wrapper .field__input {
      color: ${props => (props.isPlaceholder ? 'gray' : '#000000')};
    }
  }
`

const MonthSelect = styled(Select).withConfig({
  shouldForwardProp: prop => prop !== 'isPlaceholder'
})`
  && {
    flex-basis: 50%;
    width: 58%;
    .field__wrapper .field__input {
      color: ${props => (props.isPlaceholder ? 'gray' : '#000000')};
    }
  }
`

const CenteredRow = styled(Row)`
  align-items: center;
`

const MonthLabel = styled.label`
  margin-top: 14px;
  font-size: 0.9rem;
  margin-left: 10px;
  display: flex;
`

const Radio = styled(StyledRadioInput)`
  && {
    .field__input {
      margin-top: 30px;
    }
  }
`

function Monthly({ formik, readOnly = false }: ScheduledIntervalType & FormikValues) {
  const intl = useIntl()

  const { values, setFieldValue } = formik

  return (
    <>
      <CenteredRow>
        <MonthSelect
          disabled={readOnly}
          name={`triggerData.months`}
          label={intl.formatMessage(messages.every)}
          variant={''}
          defaultValue={-1}
          isPlaceholder={!values.triggerData.months || values.triggerData.months < 0}
        >
          <option value={-1} disabled selected hidden>
            {intl.formatMessage(messages.pleaseSelectMonth)}
          </option>
          {getNumericRangeOptions(12).map((month: any) => (
            <Select.Option key={month.value} value={month.value}>
              {month.label}
            </Select.Option>
          ))}
        </MonthSelect>
        <MonthLabel> {intl.formatMessage(messages.months)}</MonthLabel>
      </CenteredRow>
      <StyledMonthlyRadio name="triggerData.isVerbose">
        <StyledRow>
          <Radio
            disabled={readOnly}
            id={`triggerData.isVerbose-0`}
            name="triggerData.isVerbose"
            value="false"
            labelLocation="after"
          />
          <StyledSelect
            disabled={readOnly}
            name={`triggerData.day`}
            label={intl.formatMessage(messages.onDay)}
            variant={''}
            isPlaceholder={!values.triggerData.day || values.triggerData.day < 0}
            defaultValue={-1}
          >
            <option value={-1} disabled>
              {intl.formatMessage(messages.pleaseSelectDay)}
            </option>
            {getNumericRangeOptions(31).map((day: any) => (
              <Select.Option key={day.value} value={day.value}>
                {day.label}
              </Select.Option>
            ))}
          </StyledSelect>
        </StyledRow>
        <StyledRow>
          <Radio
            disabled={readOnly}
            id={`triggerData.isVerbose-1`}
            data-testid="verbose-radio"
            name="triggerData.isVerbose"
            value="true"
            labelLocation="after"
          />
          <StyledSelect
            disabled={readOnly}
            name={`triggerData.weekOfMonth`}
            label={intl.formatMessage(messages.onThe)}
            isPlaceholder={!values.triggerData.weekOfMonth || values.triggerData.weekOfMonth < 0}
            variant={''}
            defaultValue={-1}
          >
            <option value={-1} disabled selected hidden>
              {intl.formatMessage(messages.pleaseSelectWeek)}
            </option>
            {getWeekOfMonth(intl).map((wom: any) => (
              <Select.Option key={wom.value} value={wom.value}>
                {wom.label}
              </Select.Option>
            ))}
          </StyledSelect>
          <StyledSelect
            disabled={readOnly}
            name={`triggerData.dayOfWeek`}
            isPlaceholder={!values.triggerData.dayOfWeek || values.triggerData.dayOfWeek < 0}
            label="&nbsp;"
            variant={''}
            defaultValue={-1}
          >
            <option value={-1} disabled selected hidden>
              {intl.formatMessage(messages.pleaseSelectDay)}
            </option>
            {getDaysOfWeekNumeric(intl).map((dow: any) => (
              <Select.Option key={dow.value} value={dow.value}>
                {dow.label}
              </Select.Option>
            ))}
          </StyledSelect>
        </StyledRow>
      </StyledMonthlyRadio>
      <FormLabel>{intl.formatMessage(messages.at)}</FormLabel>
      <StyledDateTimePicker
        readOnly={readOnly}
        name="triggerData.time"
        showTimeOnly={true}
        showIcon={true}
        dateFormat="h:mm aa"
        placeholderText={intl.formatMessage(messages.pleaseSelectTime)}
        value={values.triggerData.time}
        onChange={setFieldValue}
      />
    </>
  )
}

export default connect(Monthly)
